/**
 * useExamAttempt Hook
 *
 * Starts or continues an exam attempt using URQL mutation.
 */

import { useMutation } from "urql";
import type { ExamAttempt } from "./useCourse";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const StartExamAttemptDocument = `
  mutation StartExamAttempt($examId: ID!) {
    startExamAttempt(examId: $examId) {
      id
      score
      maxScore
      percentage
      passed
      totalWrongAttempts
      startedAt
      completedAt
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseExamAttemptResult {
  startExam: (examId: string) => Promise<ExamAttempt | null>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Start or continue an exam attempt.
 */
export function useExamAttempt(): UseExamAttemptResult {
  const [result, executeMutation] = useMutation<{
    startExamAttempt: ExamAttempt;
  }>(StartExamAttemptDocument);

  const startExam = async (examId: string): Promise<ExamAttempt | null> => {
    const response = await executeMutation({ examId });
    if (response.error) {
      throw response.error;
    }
    return response.data?.startExamAttempt ?? null;
  };

  return {
    startExam,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
